import { Box, Button, Flex, Text, Tooltip, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { FaCheck, FaSyncAlt, FaUndo } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Layout from "../../../components/Layout";
import { ExchangeSwift } from "../../../models/ExchangeSwift";
import { getExchangeSwifts, scanExchangeSwifts, setExchangeSwiftSent } from "../../../services/exchangeSwiftService";
import { crm, Pill } from "../../../theme";
import {
  Card,
  Column,
  DataTable,
  ErrorState,
  fmtDate,
  fmtMoney,
  Loading,
  openProcessPath,
  PageHeader,
  ProcessCell,
  SegmentedTabs,
} from "../operacoes/ui";

type View = "pendentes" | "enviados" | "todos";

const fmtDateTime = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso, { setZone: true }).toFormat("dd/MM/yyyy HH:mm") : "—";

const fmtRate = (rate?: number | null) =>
  rate ? new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(rate) : "—";

const Swifts = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuthUser<any>();
  const isAdmin = !!auth?.admin;

  const [items, setItems] = useState<ExchangeSwift[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState<View>("pendentes");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const seq = useRef(0);

  const load = async () => {
    const current = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const result = await getExchangeSwifts();
      if (current !== seq.current) return;
      setItems(result);
    } catch (e) {
      if (current !== seq.current) return;
      console.log("error", e);
      setError(true);
      toast({ title: "Não foi possível carregar os swifts.", status: "error" });
    } finally {
      if (current === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSent = async (row: ExchangeSwift, sent: boolean) => {
    setSavingId(row.id);
    try {
      const result = await setExchangeSwiftSent(row.id, sent);
      if (!result.success) {
        toast({ title: result.notifications?.[0] ?? "Não foi possível alterar.", status: "error" });
        return;
      }
      await load();
    } catch {
      toast({ title: "Não foi possível alterar.", status: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const r = await scanExchangeSwifts();
      toast({
        title: r.folderFound
          ? `Pasta verificada: ${r.newFiles} novo(s), ${r.matched} casado(s), ${r.unmatched} sem parcela.`
          : "Pasta de câmbio não encontrada no servidor.",
        status: r.folderFound ? "success" : "warning",
      });
      await load();
    } catch {
      toast({ title: "Não foi possível verificar a pasta.", status: "error" });
    } finally {
      setScanning(false);
    }
  };

  const all = items ?? [];
  const rows = view === "todos" ? all : all.filter((s) => (view === "enviados" ? s.sent : !s.sent));
  const counts: Record<View, number> = {
    pendentes: all.filter((s) => !s.sent).length,
    enviados: all.filter((s) => s.sent).length,
    todos: all.length,
  };

  const columns: Column<ExchangeSwift>[] = [
    {
      header: "Processo / cliente",
      render: (r) => <ProcessCell ref_={r.processReference ?? r.processLabel} client={r.clientName ?? undefined} />,
    },
    {
      header: "Arquivo",
      render: (r) => (
        <Box maxW="260px">
          <Text fontSize="12px" noOfLines={1} title={r.fileName}>{r.fileName}</Text>
          {r.matchMessage && (
            <Text fontSize="11px" color={crm.warning} mt="2px">{r.matchMessage}</Text>
          )}
        </Box>
      ),
    },
    { header: "Apareceu em", render: (r) => <Text whiteSpace="nowrap">{fmtDateTime(r.appearedAt)}</Text> },
    {
      header: "Parcela",
      render: (r) =>
        r.installmentNumber ? (
          <Box>
            <Text fontWeight="600" whiteSpace="nowrap">{r.installmentNumber}ª · {fmtMoney(r.amount, r.currency)}</Text>
            {r.valueDate && <Text fontSize="11px" color={crm.success}>Pago em {fmtDate(r.valueDate)}</Text>}
            <Text fontSize="11px" color={crm.textMuted}>
              Contrato {r.contractNumber ?? "—"}{r.contractDate ? ` · ${fmtDate(r.contractDate)}` : ""}
            </Text>
          </Box>
        ) : (
          <Text color={crm.textFaint}>—</Text>
        ),
    },
    { header: "Taxa", isNumeric: true, render: (r) => fmtRate(r.exchangeRate) },
    {
      header: "Status",
      render: (r) =>
        r.sent ? (
          <Tooltip label={r.sentByName ? `Por ${r.sentByName}` : undefined} isDisabled={!r.sentByName}>
            <Box display="inline-block">
              <Pill bg="#f0fdf4" color={crm.success}>Enviado{r.sentAt ? ` ${fmtDate(r.sentAt)}` : ""}</Pill>
            </Box>
          </Tooltip>
        ) : (
          <Pill bg="#fffbeb" color={crm.warning}>Pendente</Pill>
        ),
    },
    {
      header: "",
      render: (r) =>
        r.sent ? (
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<FaUndo />}
            color={crm.textMuted}
            isLoading={savingId === r.id}
            onClick={(e) => { e.stopPropagation(); toggleSent(r, false); }}
          >
            Voltar p/ pendente
          </Button>
        ) : (
          <Button
            size="xs"
            bg={crm.brand}
            color="white"
            _hover={{ bg: crm.brandDark }}
            leftIcon={<FaCheck />}
            isLoading={savingId === r.id}
            onClick={(e) => { e.stopPropagation(); toggleSent(r, true); }}
          >
            Marcar enviado
          </Button>
        ),
    },
  ];

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <PageHeader
            title="Swifts"
            subtitle="Swifts da pasta de câmbio da Distribuidora. Verificada a cada 5 minutos; o swift quita a parcela (data de pagamento = data-valor do swift) e fica pendente até ser enviado ao cliente."
          >
            {isAdmin && (
              <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} isLoading={scanning} onClick={scanNow}>
                Verificar pasta agora
              </Button>
            )}
          </PageHeader>

          {!items && loading && <Loading />}
          {!items && !loading && error && <ErrorState onRetry={load} />}

          {items && (
            <Card>
              <Box mb="14px" overflowX="auto">
                <SegmentedTabs<View>
                  value={view}
                  onChange={setView}
                  items={[
                    { key: "pendentes", label: "Pendentes", count: counts.pendentes },
                    { key: "enviados", label: "Enviados", count: counts.enviados },
                    { key: "todos", label: "Todos", count: counts.todos },
                  ]}
                />
              </Box>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(r) => r.id}
                resetKey={view}
                onRowClick={(r) => r.processId && navigate(openProcessPath(r.processId))}
                emptyText={view === "pendentes" ? "Nenhum swift pendente de envio." : "Nenhum swift."}
              />
            </Card>
          )}
        </Box>
      </Layout>
    </Box>
  );
};

export default Swifts;
